import { create } from 'zustand'
import i18n, { initialLocale } from '@/i18n'
import { LOCALE_KEY, SUPPORTED_LOCALES, type Locale } from '@/i18n/config'

type LocaleState = {
  locale: Locale
  /** 用户显式切换 — 持久化到 localStorage 偏好。 */
  setLocale: (l: Locale) => void
  /** 渲染态同步（URL 规则/偏好回放）— 不落盘，避免「路过 /en/ 就改写偏好」。 */
  applyLocale: (l: Locale) => void
}

export const useLocale = create<LocaleState>((set, get) => {
  const sync = (locale: Locale, persist: boolean) => {
    if (!(SUPPORTED_LOCALES as readonly string[]).includes(locale)) return
    const prev = get().locale
    try {
      if (persist && typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(LOCALE_KEY, locale)
      }
    } catch {
      // ignore
    }
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.lang = locale
    }
    // optimistic update, revert on failure to stay in sync with i18next
    set({ locale })
    i18n.changeLanguage(locale).catch((err) => {
      console.error('[i18n] changeLanguage failed', err)
      // revert store + DOM (+ storage only when we persisted) to previous locale
      try {
        if (persist && typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem(LOCALE_KEY, prev)
        }
      } catch {}
      if (typeof document !== 'undefined' && document.documentElement) {
        document.documentElement.lang = prev
      }
      set({ locale: prev })
    })
  }
  return {
    locale: initialLocale,
    setLocale: (locale) => sync(locale, true),
    applyLocale: (locale) => sync(locale, false),
  }
})
